import { useState, useEffect, useMemo } from 'react'
import { signInWithPopup, signOut, onAuthStateChanged, type User } from 'firebase/auth'
import { getFirebaseAuth, googleProvider } from '../firebase'
import { useAnalyticsPageview } from '../hooks/useAnalyticsPageview'
import { useDocumentMeta } from '../hooks/useDocumentMeta'
import '../styles/admin.css'

type SourceType = 'auto' | 'manual'

interface DraftItem {
  id: number
  headline: string
  url: string
  source: string
  sector: string
  created_at: string
  source_type: SourceType
}

interface PublishedItem {
  id: number
  headline: string
  url: string
  source: string
  sector: string
  published_at: string
  source_type: SourceType
}

interface ArchivedItem {
  id: number
  headline: string
  url: string
  source: string
  sector: string
  published_at: string | null
  archived_at: string
  source_type: SourceType
}

// A plain outline glyph rather than the full-color 🤖 emoji — reads as a
// small icon consistent with the rest of the admin UI's minimal styling
// instead of standing out as a cartoon.
function RobotOutlineIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="8" width="16" height="12" rx="2" />
      <circle cx="9" cy="14" r="1.3" />
      <circle cx="15" cy="14" r="1.3" />
      <path d="M9 18h6" />
      <path d="M12 8V4" />
      <circle cx="12" cy="2.5" r="1" />
      <path d="M2 12v3" />
      <path d="M22 12v3" />
    </svg>
  )
}

const MANUAL_ADD_PROMPT =
  'create a comma delimited list of URLs of 5 top news articles in the Agriculture and Food field. make the list easily copyable in a code block'

// Layered/overlapping squares — the same shape Claude Code itself uses for
// its code-block copy affordance.
function CopyIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

// Shared by all three tables — an outline robot glyph for RSS-curated
// rows, ✍️ for ones typed into the manual-add form above. A title
// attribute carries the same meaning for anyone hovering.
function SourceTypeIcon({ sourceType }: { sourceType: SourceType }) {
  return (
    <span className="source-type-icon" title={sourceType === 'manual' ? 'Added manually' : 'Auto-curated'}>
      {sourceType === 'manual' ? '✍️' : <RobotOutlineIcon />}
    </span>
  )
}

type Tab = 'pending' | 'published' | 'archived'

const TABS: { value: Tab; label: string }[] = [
  { value: 'pending', label: 'Pending Review' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
]

// Shared sorting for all three dashboard tables — click a column header to
// sort by it, click again to flip direction. Source/Sector default to A-Z;
// the date column (whichever one applies per table — Created, Published,
// or Archived) defaults to newest-first.
type SortField = 'source' | 'sector' | 'date'
type SortDirection = 'asc' | 'desc'
interface SortState {
  field: SortField
  direction: SortDirection
}
const DEFAULT_SORT: SortState = { field: 'date', direction: 'desc' }

function toggleSort(current: SortState, field: SortField): SortState {
  if (current.field === field) {
    return { field, direction: current.direction === 'asc' ? 'desc' : 'asc' }
  }
  return { field, direction: field === 'date' ? 'desc' : 'asc' }
}

function sortItems<T extends { source: string; sector: string }>(
  items: T[],
  sort: SortState,
  dateValue: (item: T) => string | null
): T[] {
  const copy = [...items]
  copy.sort((a, b) => {
    const cmp =
      sort.field === 'source'
        ? a.source.localeCompare(b.source)
        : sort.field === 'sector'
          ? a.sector.localeCompare(b.sector)
          : (dateValue(a) ?? '').localeCompare(dateValue(b) ?? '')
    return sort.direction === 'asc' ? cmp : -cmp
  })
  return copy
}

function SortableTh({
  label,
  field,
  sort,
  onSort,
}: {
  label: string
  field: SortField
  sort: SortState
  onSort: (field: SortField) => void
}) {
  const active = sort.field === field
  return (
    <th className={`sortable-th ${active ? 'active' : ''}`} onClick={() => onSort(field)}>
      {label}
      {/* Always-visible ⇅ hints every column here is sortable; swaps for a
          bold ▲/▼ once it's the active sort, showing current direction. */}
      <span className="sort-indicator">{active ? (sort.direction === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  )
}

const ALLOWED_EMAIL_DOMAIN = '@selciuspartners.com'

// Calls getFirebaseAuth() fresh each time rather than caching it at module
// scope — this module is imported (and evaluated) before main.tsx's
// bootstrap() has run initFirebaseAuth(), so a module-level `const auth =
// getFirebaseAuth()` would permanently capture null.
async function authFetch(url: string, options: RequestInit = {}) {
  const auth = getFirebaseAuth()
  const headers = new Headers(options.headers)
  const token = await auth?.currentUser?.getIdToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  return fetch(url, { ...options, headers })
}

// Used by both the Pending Review and Published tables — click the pencil
// to edit in place, Enter/✓ to save, Escape/✕ to cancel. Exists mainly for
// the case where no title could be fetched (manual-add falls back to the
// raw URL as the headline), but works on any row.
function EditableHeadline({
  id,
  headline,
  url,
  sourceType,
  onSaved,
}: {
  id: number
  headline: string
  url: string
  sourceType: SourceType
  onSaved: (id: number, headline: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(headline)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setValue(headline)
  }, [headline])

  const save = async () => {
    const trimmed = value.trim()
    if (!trimmed || trimmed === headline) {
      setEditing(false)
      setValue(headline)
      return
    }
    setSaving(true)
    try {
      const res = await authFetch(`/api/admin/insights-items/${id}/headline`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headline: trimmed }),
      })
      if (!res.ok) throw new Error('Failed to update headline')
      onSaved(id, trimmed)
      setEditing(false)
    } catch (error) {
      console.error('Error updating headline:', error)
      window.alert('Failed to update the title — please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div className="headline-edit">
        <input
          className="headline-edit-input"
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') save()
            if (e.key === 'Escape') {
              setEditing(false)
              setValue(headline)
            }
          }}
          disabled={saving}
          autoFocus
        />
        <button className="headline-edit-save" onClick={save} disabled={saving} title="Save">
          ✓
        </button>
        <button
          className="headline-edit-cancel"
          onClick={() => {
            setEditing(false)
            setValue(headline)
          }}
          disabled={saving}
          title="Cancel"
        >
          ✕
        </button>
      </div>
    )
  }

  return (
    <span className="headline-display">
      <SourceTypeIcon sourceType={sourceType} />
      <a href={url} target="_blank" rel="noopener noreferrer">
        {headline}
      </a>
      <button className="headline-edit-btn" onClick={() => setEditing(true)} title="Edit title">
        ✎
      </button>
    </span>
  )
}

export default function AdminPage() {
  useAnalyticsPageview()
  useDocumentMeta('Admin | Selcius Partners', { noindex: true })
  // Safe to read directly here (unlike module scope above): by the time
  // this component first renders, bootstrap() in main.tsx has already
  // resolved, so initFirebaseAuth() has run if it's going to.
  const auth = getFirebaseAuth()
  const [user, setUser] = useState<User | null>(null)
  const [authChecked, setAuthChecked] = useState(!auth) // true immediately in dev
  const [loginError, setLoginError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('pending')
  const [drafts, setDrafts] = useState<DraftItem[]>([])
  const [published, setPublished] = useState<PublishedItem[]>([])
  const [archived, setArchived] = useState<ArchivedItem[]>([])
  const [loading, setLoading] = useState(false)
  const [publishedLoading, setPublishedLoading] = useState(false)
  const [archivedLoading, setArchivedLoading] = useState(false)
  const [actionInProgress, setActionInProgress] = useState<number | null>(null)
  const [draftSort, setDraftSort] = useState<SortState>(DEFAULT_SORT)
  const [archivedSort, setArchivedSort] = useState<SortState>(DEFAULT_SORT)

  const [sectors, setSectors] = useState<string[]>([])
  const [manualUrls, setManualUrls] = useState('')
  const [manualSector, setManualSector] = useState('')
  const [manualAutoPublish, setManualAutoPublish] = useState(true)
  const [manualSubmitting, setManualSubmitting] = useState(false)
  const [manualError, setManualError] = useState<string | null>(null)
  const [manualSummary, setManualSummary] = useState<string[] | null>(null)
  const [promptCopied, setPromptCopied] = useState(false)

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(MANUAL_ADD_PROMPT)
      setPromptCopied(true)
      setTimeout(() => setPromptCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy prompt:', error)
    }
  }

  // Published's order comes straight from the server (display_order,
  // drag-and-drop-editable — see reorder handlers below) rather than a
  // client-side sort, so unlike drafts/archived there's no sortedPublished
  // derivation or column-click sorting for this table.
  const [draggedId, setDraggedId] = useState<number | null>(null)

  const sortedDrafts = useMemo(() => sortItems(drafts, draftSort, d => d.created_at), [drafts, draftSort])
  const sortedArchived = useMemo(
    () => sortItems(archived, archivedSort, a => a.archived_at),
    [archived, archivedSort]
  )

  const isLoggedIn = !auth || !!user // dev bypass or real Firebase user

  useEffect(() => {
    if (!auth) return
    return onAuthStateChanged(auth, u => {
      setUser(u)
      setAuthChecked(true)
    })
  }, [])

  // All three lists are fetched upfront rather than per-tab-switch — the
  // dataset is small (weekly curation batches), and this keeps tab
  // switching instant with no loading flash.
  useEffect(() => {
    if (isLoggedIn) {
      fetchDrafts()
      fetchPublished()
      fetchArchived()
    }
  }, [isLoggedIn])

  useEffect(() => {
    fetch('/api/sectors')
      .then(res => res.json())
      .then((list: string[]) => {
        setSectors(list)
        setManualSector(prev => prev || list[0] || '')
      })
      .catch(err => console.error('Failed to fetch sectors:', err))
  }, [])

  // Extracts every http(s) URL found anywhere in the pasted text, rather
  // than splitting on commas and treating each piece as a URL. That
  // earlier approach broke on anything pasted alongside the URLs (a lead-in
  // sentence, numbering, line breaks with no comma) — the first "piece"
  // would glue leading text onto the first real URL and fail to parse, a
  // bug that showed up as every batch's first entry failing regardless of
  // which URL it actually was. Matching http(s):// tokens directly instead
  // means any surrounding prose, newlines, or numbering is simply ignored,
  // not fed in and mis-parsed. Still respects the comma-delimited case (a
  // comma right after a URL is naturally excluded from the match) and
  // works with or without spaces around delimiters, same as before.
  const parseManualUrls = (raw: string): string[] => {
    const matches = raw.match(/https?:\/\/[^\s,]+/g)
    return matches ? [...new Set(matches)] : []
  }

  // The backend returns the full row (including status, which decides
  // which list it belongs in) — DraftItem/PublishedItem don't declare that
  // field themselves since each is only ever fetched from an
  // already-status-filtered endpoint, so it's added on here instead.
  type ManualAddResult =
    | { url: string; success: true; item: (DraftItem | PublishedItem) & { status: string }; warning?: string }
    | { url: string; success: false; error: string }

  const handleManualAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    setManualError(null)
    setManualSummary(null)
    const urls = parseManualUrls(manualUrls)
    if (urls.length === 0 || !manualSector) return

    setManualSubmitting(true)
    try {
      const res = await authFetch('/api/admin/insights-items/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls, sector: manualSector, autoPublish: manualAutoPublish }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to add items')

      const results = data.results as ManualAddResult[]

      // Drop each success straight into its matching list rather than a
      // full refetch — same pattern as handlePublish/handleArchive, and
      // gives the "shows up immediately" behavior directly instead of
      // round-tripping. Newest-added goes on top within this batch.
      const newPublished = results.filter((r): r is Extract<ManualAddResult, { success: true }> => r.success && r.item.status === 'published')
      const newDrafts = results.filter((r): r is Extract<ManualAddResult, { success: true }> => r.success && r.item.status !== 'published')
      if (newPublished.length) setPublished(prev => [...newPublished.map(r => r.item as PublishedItem), ...prev])
      if (newDrafts.length) setDrafts(prev => [...newDrafts.map(r => r.item as DraftItem), ...prev])

      const summary = results.map(r =>
        r.success
          ? `✓ ${r.item.headline}${r.warning ? ` — ${r.warning}` : ''}`
          : `✗ ${r.url} — ${r.error}`
      )
      setManualSummary(summary)

      if (newPublished.length || newDrafts.length) {
        setManualUrls('')
        setActiveTab(newPublished.length ? 'published' : 'pending')
      }
    } catch (error) {
      setManualError(error instanceof Error ? error.message : 'Failed to add items')
    } finally {
      setManualSubmitting(false)
    }
  }

  const handleHeadlineSaved = (id: number, headline: string) => {
    setDrafts(prev => prev.map(d => (d.id === id ? { ...d, headline } : d)))
    setPublished(prev => prev.map(p => (p.id === id ? { ...p, headline } : p)))
  }

  // Drag-and-drop reorder for the Published tab. Reorders the local array
  // live as the dragged row passes over another (the standard "list
  // reorder" pattern), then persists the final order on drop — a failed
  // save just logs, since the list already reflects the intended order
  // and a hard refresh would re-fetch the last successfully saved one.
  const handleDragStart = (id: number) => setDraggedId(id)

  const handleDragOver = (e: React.DragEvent, overId: number) => {
    e.preventDefault()
    if (draggedId === null || draggedId === overId) return
    setPublished(prev => {
      const fromIndex = prev.findIndex(p => p.id === draggedId)
      const toIndex = prev.findIndex(p => p.id === overId)
      if (fromIndex === -1 || toIndex === -1) return prev
      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
  }

  const handleDragEnd = async () => {
    setDraggedId(null)
    try {
      const res = await authFetch('/api/admin/insights-items/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: published.map(p => p.id) }),
      })
      if (!res.ok) throw new Error('Failed to save order')
    } catch (error) {
      console.error('Error saving reorder:', error)
    }
  }

  const fetchDrafts = async () => {
    setLoading(true)
    try {
      const res = await authFetch('/api/admin/insights-items')
      if (!res.ok) throw new Error('Failed to fetch drafts')
      setDrafts(await res.json())
    } catch (error) {
      console.error('Error fetching drafts:', error)
      setDrafts([])
    } finally {
      setLoading(false)
    }
  }

  const fetchPublished = async () => {
    setPublishedLoading(true)
    try {
      const res = await authFetch('/api/admin/insights-items?status=published')
      if (!res.ok) throw new Error('Failed to fetch published items')
      setPublished(await res.json())
    } catch (error) {
      console.error('Error fetching published items:', error)
      setPublished([])
    } finally {
      setPublishedLoading(false)
    }
  }

  const fetchArchived = async () => {
    setArchivedLoading(true)
    try {
      const res = await authFetch('/api/admin/insights-items?status=archived')
      if (!res.ok) throw new Error('Failed to fetch archived items')
      setArchived(await res.json())
    } catch (error) {
      console.error('Error fetching archived items:', error)
      setArchived([])
    } finally {
      setArchivedLoading(false)
    }
  }

  const handleLogin = async () => {
    if (!auth) return
    setLoginError(null)

    try {
      const result = await signInWithPopup(auth, googleProvider)
      if (!result.user.email?.endsWith(ALLOWED_EMAIL_DOMAIN)) {
        await signOut(auth)
        setLoginError(`Access restricted to ${ALLOWED_EMAIL_DOMAIN} accounts`)
      }
    } catch (error) {
      setLoginError('Sign-in failed: ' + (error instanceof Error ? error.message : 'Unknown error'))
    }
  }

  const handlePublish = async (id: number) => {
    setActionInProgress(id)
    try {
      const res = await authFetch(`/api/admin/insights-items/${id}/publish`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to publish')
      const { item } = (await res.json()) as { item: PublishedItem }
      setDrafts(prev => prev.filter(d => d.id !== id))
      setPublished(prev => [item, ...prev])
    } catch (error) {
      console.error('Publish error:', error)
    } finally {
      setActionInProgress(null)
    }
  }

  const handleReject = async (id: number) => {
    setActionInProgress(id)
    try {
      const res = await authFetch(`/api/admin/insights-items/${id}/reject`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to reject')
      setDrafts(prev => prev.filter(d => d.id !== id))
    } catch (error) {
      console.error('Reject error:', error)
    } finally {
      setActionInProgress(null)
    }
  }

  const handleArchive = async (id: number) => {
    setActionInProgress(id)
    try {
      const res = await authFetch(`/api/admin/insights-items/${id}/archive`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to archive')
      const { item } = (await res.json()) as { item: ArchivedItem }
      setPublished(prev => prev.filter(p => p.id !== id))
      setArchived(prev => [item, ...prev])
    } catch (error) {
      console.error('Archive error:', error)
    } finally {
      setActionInProgress(null)
    }
  }

  const handleRestore = async (id: number) => {
    setActionInProgress(id)
    try {
      const res = await authFetch(`/api/admin/insights-items/${id}/restore`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to restore')
      const { item } = (await res.json()) as { item: PublishedItem }
      setArchived(prev => prev.filter(a => a.id !== id))
      setPublished(prev => [item, ...prev])
    } catch (error) {
      console.error('Restore error:', error)
    } finally {
      setActionInProgress(null)
    }
  }

  // Shared by the Published and Archived tabs — filters both lists (a
  // harmless no-op on whichever one doesn't contain the id) rather than
  // needing to know which tab the click came from.
  const handleDelete = async (id: number, headline: string) => {
    if (!window.confirm(`Permanently remove "${headline}"? This cannot be undone.`)) {
      return
    }
    setActionInProgress(id)
    try {
      const res = await authFetch(`/api/admin/insights-items/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Failed to delete')
      setPublished(prev => prev.filter(p => p.id !== id))
      setArchived(prev => prev.filter(a => a.id !== id))
    } catch (error) {
      console.error('Delete error:', error)
    } finally {
      setActionInProgress(null)
    }
  }

  const handleLogout = async () => {
    if (!auth) return
    try {
      await signOut(auth)
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  if (!authChecked) {
    return <div className="admin-login" />
  }

  if (!isLoggedIn) {
    return (
      <div className="admin-login">
        <div className="login-box">
          <h1>Admin Login</h1>
          <p className="login-hint">Sign in with your {ALLOWED_EMAIL_DOMAIN} Google account</p>
          <button onClick={handleLogin} className="google-signin-btn">
            Sign in with Google
          </button>
          {loginError && <div className="error">{loginError}</div>}
        </div>
      </div>
    )
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h1>Insights Dashboard</h1>
        <div className="admin-header-right">
          <a href="/insights" className="view-site-link">
            View Insights page
          </a>
          {user && <span className="signed-in-as">{user.email}</span>}
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="admin-container">
        <details className="manual-add-guide" open>
          <summary>Add your own researched news articles</summary>
          <p>
            To use: from your Claude.ai chat session, paste the following prompt:
          </p>
          <div className="manual-add-guide-prompt-wrap">
            <code className="manual-add-guide-prompt">{MANUAL_ADD_PROMPT}</code>
            <button
              type="button"
              className="manual-add-guide-copy"
              onClick={handleCopyPrompt}
              title="Copy prompt"
            >
              <CopyIcon />
              {promptCopied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p>
            Replace <strong>Agriculture and Food</strong> with whichever category you're researching. In the
            resulting list of websites, click the copy icon in the top-right, then paste it into the URL field
            below.
          </p>
        </details>

        <form className="manual-add-form" onSubmit={handleManualAdd}>
          <div className="manual-add-row">
            <textarea
              className="manual-add-url"
              placeholder="https://example.com/article-one, https://example.com/article-two"
              value={manualUrls}
              onChange={e => setManualUrls(e.target.value)}
              maxLength={500}
              rows={2}
              required
            />
            <div className="manual-add-controls">
              <select
                className="manual-add-sector"
                value={manualSector}
                onChange={e => setManualSector(e.target.value)}
                required
              >
                {sectors.map(s => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <label className="manual-add-checkbox">
                <input
                  type="checkbox"
                  checked={manualAutoPublish}
                  onChange={e => setManualAutoPublish(e.target.checked)}
                />
                Auto-publish
              </label>
              <button type="submit" className="btn-publish manual-add-submit" disabled={manualSubmitting}>
                {manualSubmitting ? 'Adding…' : 'Add URL(s)'}
              </button>
            </div>
          </div>
          <p className="manual-add-hint">
            One URL, or several separated by commas (spaces around commas are fine either way — up to 500
            characters).{' '}
            {manualAutoPublish
              ? 'Goes live immediately on the public Insights page.'
              : 'Goes to Pending Review for approval before it’s public.'}
          </p>
          {manualError && <div className="error manual-add-error">{manualError}</div>}
          {manualSummary && (
            <ul className="manual-add-summary">
              {manualSummary.map((line, i) => (
                <li key={i} className={line.startsWith('✗') ? 'manual-add-fail' : 'manual-add-ok'}>
                  {line}
                </li>
              ))}
            </ul>
          )}
        </form>

        <nav className="admin-tabs">
          {TABS.map(tab => {
            const count =
              tab.value === 'pending' ? drafts.length : tab.value === 'published' ? published.length : archived.length
            return (
              <button
                key={tab.value}
                className={`admin-tab ${activeTab === tab.value ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.value)}
              >
                {tab.label} <span className="admin-tab-count">{count}</span>
              </button>
            )
          })}
        </nav>

        <section className="admin-section" hidden={activeTab !== 'pending'}>
          {loading ? (
            <div>Loading drafts...</div>
          ) : drafts.length === 0 ? (
            <div className="empty">No pending drafts.</div>
          ) : (
            <div className="drafts-table">
              <table>
                <thead>
                  <tr>
                    <th>Headline</th>
                    <SortableTh label="Source" field="source" sort={draftSort} onSort={f => setDraftSort(s => toggleSort(s, f))} />
                    <SortableTh label="Sector" field="sector" sort={draftSort} onSort={f => setDraftSort(s => toggleSort(s, f))} />
                    <SortableTh label="Created" field="date" sort={draftSort} onSort={f => setDraftSort(s => toggleSort(s, f))} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDrafts.map(draft => (
                    <tr key={draft.id}>
                      <td className="headline">
                        <EditableHeadline
                          id={draft.id}
                          headline={draft.headline}
                          url={draft.url}
                          sourceType={draft.source_type}
                          onSaved={handleHeadlineSaved}
                        />
                      </td>
                      <td>{draft.source}</td>
                      <td>{draft.sector}</td>
                      <td>{new Date(draft.created_at).toLocaleDateString()}</td>
                      <td className="actions">
                        <button
                          onClick={() => handlePublish(draft.id)}
                          disabled={actionInProgress === draft.id}
                          className="btn-publish"
                        >
                          Publish
                        </button>
                        <button
                          onClick={() => handleReject(draft.id)}
                          disabled={actionInProgress === draft.id}
                          className="btn-reject"
                        >
                          Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="admin-section" hidden={activeTab !== 'published'}>
          {publishedLoading ? (
            <div>Loading published items...</div>
          ) : published.length === 0 ? (
            <div className="empty">Nothing published yet.</div>
          ) : (
            <div className="drafts-table">
              <p className="drag-hint">⠿ Drag rows to reorder how they appear on the public Insights page.</p>
              <table>
                <thead>
                  <tr>
                    <th className="drag-col"></th>
                    <th>Headline</th>
                    <th>Source</th>
                    <th>Sector</th>
                    <th>Published</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {published.map(item => (
                    <tr
                      key={item.id}
                      draggable
                      onDragStart={() => handleDragStart(item.id)}
                      onDragOver={e => handleDragOver(e, item.id)}
                      onDrop={e => e.preventDefault()}
                      onDragEnd={handleDragEnd}
                      className={draggedId === item.id ? 'dragging' : ''}
                    >
                      <td className="drag-col" title="Drag to reorder">
                        ⠿
                      </td>
                      <td className="headline">
                        <EditableHeadline
                          id={item.id}
                          headline={item.headline}
                          url={item.url}
                          sourceType={item.source_type}
                          onSaved={handleHeadlineSaved}
                        />
                      </td>
                      <td>{item.source}</td>
                      <td>{item.sector}</td>
                      <td>{item.published_at ? new Date(item.published_at).toLocaleDateString() : '—'}</td>
                      <td className="actions">
                        <button
                          onClick={() => handleArchive(item.id)}
                          disabled={actionInProgress === item.id}
                          className="btn-archive"
                        >
                          Archive
                        </button>
                        <button
                          onClick={() => handleDelete(item.id, item.headline)}
                          disabled={actionInProgress === item.id}
                          className="btn-delete"
                        >
                          Remove Permanently
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="admin-section" hidden={activeTab !== 'archived'}>
          {archivedLoading ? (
            <div>Loading archived items...</div>
          ) : archived.length === 0 ? (
            <div className="empty">No archived items.</div>
          ) : (
            <div className="drafts-table">
              <table>
                <thead>
                  <tr>
                    <th>Headline</th>
                    <SortableTh label="Source" field="source" sort={archivedSort} onSort={f => setArchivedSort(s => toggleSort(s, f))} />
                    <SortableTh label="Sector" field="sector" sort={archivedSort} onSort={f => setArchivedSort(s => toggleSort(s, f))} />
                    <SortableTh label="Archived" field="date" sort={archivedSort} onSort={f => setArchivedSort(s => toggleSort(s, f))} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedArchived.map(item => (
                    <tr key={item.id}>
                      <td className="headline">
                        <span className="headline-display">
                          <SourceTypeIcon sourceType={item.source_type} />
                          <a href={item.url} target="_blank" rel="noopener noreferrer">
                            {item.headline}
                          </a>
                        </span>
                      </td>
                      <td>{item.source}</td>
                      <td>{item.sector}</td>
                      <td>{item.archived_at ? new Date(item.archived_at).toLocaleDateString() : '—'}</td>
                      <td className="actions">
                        <button
                          onClick={() => handleRestore(item.id)}
                          disabled={actionInProgress === item.id}
                          className="btn-publish"
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => handleDelete(item.id, item.headline)}
                          disabled={actionInProgress === item.id}
                          className="btn-delete"
                        >
                          Remove Permanently
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
